import * as React from "react";

import { cn } from "@/lib/utils";

export interface FieldBorderProps extends React.FieldsetHTMLAttributes<HTMLFieldSetElement> {
  label: React.ReactNode;
  htmlFor?: string;
  invalid?: boolean;
}

/**
 * Wraps a form control in a bordered box with its label set into the top
 * border (native <fieldset>/<legend> "notch"), instead of stacking the label
 * above the control.
 */
const FieldBorder = React.forwardRef<HTMLFieldSetElement, FieldBorderProps>(
  ({ label, htmlFor, invalid, className, children, ...props }, ref) => (
    <fieldset
      ref={ref}
      className={cn(
        "min-w-0 rounded-md border border-input bg-background px-3 pb-2 pt-0 shadow-sm",
        "transition-[border-color,box-shadow] duration-150",
        "has-[:focus-visible]:border-primary has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-primary/20",
        "disabled:cursor-not-allowed disabled:opacity-50",
        invalid && "border-destructive",
        className
      )}
      {...props}
    >
      <legend className="-ml-1 px-1 text-xs font-medium leading-none text-muted-foreground">
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
      </legend>
      {children}
    </fieldset>
  )
);
FieldBorder.displayName = "FieldBorder";

/** Shared classes for form controls nested inside a FieldBorder, so the control itself renders borderless/flush. */
export const fieldBorderInputClassName =
  "h-6 w-full border-0 bg-transparent p-0 shadow-none focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0";

export { FieldBorder };
